/**
 * La pagina di un genere (SPEC §3.7): percorso, sottocategorie, ordinamento, griglia verticale.
 *
 * ## Cosa cambia rispetto a prima
 *
 * Prima «entrare in un genere» voleva dire **ridurre la vetrina a una fascia**: una riga orizzontale
 * che si sfoglia a destra. Su una categoria da duecento titoli quella riga è un corridoio — per
 * arrivare al centesimo si preme ottanta volte, e non si ha mai idea di quanti siano. La griglia
 * scorre in verticale, mostra quattro o cinque titoli per riga, e chi guarda vede subito **quanto**
 * c'è.
 *
 * ## L'ordinamento è una decisione di chi guarda, non una nostra
 *
 * «Novità» è il predefinito perché è quello che risponde alla domanda più comune — «c'è qualcosa di
 * nuovo?» — e perché è l'unico ordine che il catalogo dichiara da sé. «A–Z» esiste perché su una
 * categoria grande il titolo che si cerca si cerca per nome.
 *
 * **«Più visti» non c'è, ed è una mancanza dichiarata.** Il prototipo la prevede e la piattaforma non
 * ha il dato: l'avanzamento di visione sta in `lines.app_data`, una colonna `jsonb` scritta per
 * essere letta tutta insieme da una linea sola — la sua stessa migrazione lo dice, «il giorno in cui
 * servisse una statistica sui titoli più ripresi, questa colonna non aiuta». Offrire la pastiglia
 * ordinando per voto, o per data, o per qualunque cosa che «più visti» non è, darebbe un elenco che
 * mente in un modo che nessuno può verificare guardandolo. Torna quando quel conteggio esiste.
 */

import { compareCatalogNames } from './catalogSort.js';
import { watchStateOf, type VodItem, type WatchProgress, type WatchState } from './catalog.js';
import { idsUnder, nodeAt, trailAt, type CategoryNode } from './categoryTree.js';

/** Come si ordina la griglia. Vedi la nota in testa al modulo per «più visti». */
export type GenreSort = 'newest' | 'alphabetical';

export const GENRE_SORTS: ReadonlyArray<GenreSort> = ['newest', 'alphabetical'];

/** Una voce del percorso in cima: «FILM › AZIONE ›». L'ultima è il nodo corrente e non si mostra. */
export interface GenreCrumb {
  /** Vuoto per la radice della sezione («Film», «Serie TV»), che non è una categoria. */
  readonly id: string;
  readonly name: string;
  /** Il percorso a cui riporta, premendola. Vuoto = torna alla vetrina. */
  readonly path: ReadonlyArray<string>;
}

/** Una pastiglia di sottocategoria. */
export interface GenreChild {
  readonly id: string;
  readonly name: string;
  readonly count: number;
  /** Vero quando **questa** ha a sua volta dei figli: nel disegno è il «›» in coda al nome. */
  readonly hasChildren: boolean;
}

export interface GenreEntry {
  readonly item: VodItem;
  readonly state: WatchState;
}

export interface GenrePage {
  /** `null` quando il percorso non si risolve: il catalogo è cambiato sotto i piedi. */
  readonly node: CategoryNode | null;
  readonly crumbs: ReadonlyArray<GenreCrumb>;
  /** Vuoto quando il nodo non ha figli: la fila di pastiglie non si disegna affatto. */
  readonly children: ReadonlyArray<GenreChild>;
  readonly items: ReadonlyArray<GenreEntry>;
  /** Quanti titoli ha il nodo **prima** del filtro «solo non visti»: è il numero accanto al titolo. */
  readonly total: number;
}

export interface GenrePageInput {
  readonly roots: ReadonlyArray<CategoryNode>;
  /** Il percorso aperto, dalla radice: `['azione', 'spionaggio']`. */
  readonly path: ReadonlyArray<string>;
  readonly items: ReadonlyArray<VodItem>;
  readonly progress: WatchProgress;
  readonly sort: GenreSort;
  /** Il filtro: esclude i titoli al 100%. */
  readonly unwatchedOnly: boolean;
  /** Come si chiama la sezione — «Film», «Serie TV» — per la prima voce del percorso. */
  readonly sectionName: string;
}

export function genrePage(input: GenrePageInput): GenrePage {
  const node = nodeAt(input.roots, input.path);

  /*
    La prima voce del percorso è la **sezione**, non una categoria: «Film» riporta alla vetrina.

    C'è sempre, anche a un livello solo, e non è ridondante: è l'unico modo di sapere se «Thriller»
    sono i film o le serie. Il tasto Indietro non lo dice, e le due sezioni hanno categorie con gli
    stessi nomi.
  */
  const sectionCrumb: GenreCrumb = { id: '', name: input.sectionName, path: [] };

  if (node === null) {
    return { node: null, crumbs: [sectionCrumb], children: [], items: [], total: 0 };
  }

  const trail = trailAt(input.roots, input.path);
  const crumbs: GenreCrumb[] = [sectionCrumb];
  // Gli antenati, **non** il nodo corrente: quello è già il titolo grande sotto il percorso, e
  // ripeterlo era il difetto che si vedeva entrando in una categoria.
  for (let level = 0; level < trail.length - 1; level += 1) {
    const ancestor = trail[level] as CategoryNode;
    crumbs.push({ id: ancestor.id, name: ancestor.name, path: input.path.slice(0, level + 1) });
  }

  const children: GenreChild[] = node.children.map((child) => ({
    id: child.id,
    name: child.name,
    count: child.count,
    hasChildren: child.children.length > 0,
  }));

  // Il nodo **e tutta la sua discendenza**: un titolo di «Spionaggio» compare anche aprendo
  // «Azione», altrimenti i nodi che raggruppano sembrano vuoti proprio perché raggruppano.
  const wanted = idsUnder(node);
  const mine = input.items.filter((item) => wanted.has(item.categoryId));

  const decorated: GenreEntry[] = mine.map((item) => ({
    item,
    state: watchStateOf(item, input.progress),
  }));

  const visible = input.unwatchedOnly ? decorated.filter((entry) => !entry.state.watched) : decorated;

  return {
    node,
    crumbs,
    children,
    items: sortEntries(visible, input.sort),
    // **Prima** del filtro: è il conteggio del genere, non di quello che si sta guardando adesso.
    // Con il filtro acceso i due divergono, ed è giusto — il titolo della pagina dice quanto c'è.
    total: decorated.length,
  };
}

function sortEntries(entries: ReadonlyArray<GenreEntry>, sort: GenreSort): ReadonlyArray<GenreEntry> {
  const out = [...entries];
  if (sort === 'alphabetical') {
    // Per nome di catalogo: emoji e punteggiatura iniziale non contano, perché i nomi veri di questo
    // catalogo cominciano quasi tutti per emoji e ordinarle ordina le emoji.
    out.sort((a, b) => compareCatalogNames(a.item.title, b.item.title));
    return out;
  }

  /*
    «Novità»: chi non dichiara un istante d'inserimento va **in fondo**, non in testa.

    Su un catalogo importato da un fornitore il campo manca a macchia, e trattare «non lo so» come
    «adessissimo» metterebbe in cima ai nuovi arrivi esattamente i titoli di cui non si sa niente.
    Stessa regola di `latestArrivals`, e con lo stesso comparatore a due `if` invece di una
    sottrazione: `null - null` dà `NaN`, e un comparatore che torna `NaN` lascia l'ordine a
    discrezione del motore — cioè una griglia che balla fra due disegni senza che nulla sia cambiato.
  */
  out.sort((a, b) => {
    const left = a.item.addedAtMs;
    const right = b.item.addedAtMs;
    if (left === right) return compareCatalogNames(a.item.title, b.item.title);
    if (left === null) return 1;
    if (right === null) return -1;
    if (left === right) return 0;
    return right - left;
  });
  return out;
}
